import { useEffect, useRef, useState } from "react";
import { gradeCriteriaLabel } from "../grades";
import { matchLabel } from "../search";
import type { CtcaeTerm, GradeEvaluation, MatchType, ParsedQuery, ReferenceKey, ReferenceValues } from "../types";
import { GradeCards } from "./GradeCards";
import { SourceText } from "./SourceText";

type Props = {
  record: CtcaeTerm;
  matchType: MatchType;
  parsedQuery: ParsedQuery;
  evaluation: GradeEvaluation;
  references: ReferenceValues;
  onReferenceValue: (key: ReferenceKey, value?: number) => void;
  onValueChange: (value?: number, unit?: string) => void;
  saved: boolean;
  onToggleSaved: () => void;
  onOpenReference: (id: string) => void;
  onOpenPdf: () => void;
};

export function ResultDetail({
  record,
  matchType,
  parsedQuery,
  evaluation,
  references,
  onReferenceValue,
  onValueChange,
  saved,
  onToggleSaved,
  onOpenReference,
  onOpenPdf,
}: Props) {
  const [valueInput, setValueInput] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [valueError, setValueError] = useState("");
  const valueInputRef = useRef<HTMLInputElement>(null);

  const numericGrading = record.numericGrading;
  const defaultUnit = numericGrading?.acceptedUnits.includes(numericGrading.defaultUnit)
    ? numericGrading.defaultUnit
    : "";

  useEffect(() => {
    if (document.activeElement === valueInputRef.current) return;
    setValueInput(parsedQuery.numericValue?.toString() ?? "");
    setSelectedUnit(parsedQuery.unit ?? defaultUnit);
    setValueError("");
  }, [record.id, parsedQuery.numericValue, parsedQuery.unit, defaultUnit]);

  const updateGradeValue = (rawValue: string, unit: string) => {
    const normalized = rawValue.trim().replace(/,/g, "");
    if (!normalized) {
      setValueError("");
      onValueChange(undefined);
      return;
    }
    const value = Number(normalized);
    if (!Number.isFinite(value)) {
      setValueError("Enter a valid numeric value.");
      onValueChange(undefined);
      return;
    }
    setValueError("");
    onValueChange(value, unit || undefined);
  };

  const changeValue = (rawValue: string) => {
    setValueInput(rawValue);
    updateGradeValue(rawValue, selectedUnit);
  };

  const changeUnit = (unit: string) => {
    setSelectedUnit(unit);
    updateGradeValue(valueInput, unit);
  };

  const referencesToShow = record.numericGrading?.references ?? [];

  return (
    <article className="term-detail" aria-labelledby="term-title">
      {matchType === "other-specify" && (
        <div className="category-notice">
          Your search does not appear to match a dedicated CTCAE term in this version. This may fit under the following CTCAE category.
        </div>
      )}

      <div className={`term-overview${numericGrading ? " has-value-entry" : ""}`}>
        <header className="term-heading">
          <span className={`match-badge ${matchType}`}>{matchLabel(matchType)}</span>
          <div className="term-title-row">
            <h1 id="term-title">{record.term}</h1>
            <button className={`save-button${saved ? " saved" : ""}`} type="button" onClick={onToggleSaved} aria-pressed={saved}>
              <span aria-hidden="true">{saved ? "★" : "☆"}</span> {saved ? "Saved" : "Save"}
            </button>
          </div>
          <p className="term-definition"><SourceText text={record.definition} /></p>
          <p className="term-meta">{record.soc} <span>·</span> CTCAE v{record.version}</p>
        </header>

        {numericGrading && (
          <section className="value-panel" aria-labelledby="value-heading">
            <div className="value-toolbar">
              <div className="value-metric">
                <span className="section-kicker">Grade by value</span>
                <strong id="value-heading">{numericGrading.metric}</strong>
              </div>
              <div className="value-form">
                <label className="value-input-label">
                  <span>Value</span>
                  <input
                    ref={valueInputRef}
                    type="text"
                    inputMode="decimal"
                    value={valueInput}
                    onChange={(event) => changeValue(event.target.value)}
                    placeholder="Enter value"
                    aria-describedby={valueError ? "value-input-error" : undefined}
                    aria-invalid={Boolean(valueError)}
                    autoComplete="off"
                  />
                </label>
                <label className="unit-select-label">
                  <span>Unit</span>
                  <select value={selectedUnit} onChange={(event) => changeUnit(event.target.value)}>
                    {!defaultUnit && <option value="">Auto-detect</option>}
                    {numericGrading.acceptedUnits.map((unit) => <option value={unit} key={unit}>{unit}</option>)}
                  </select>
                </label>
              </div>
            </div>
            {valueError && <p className="value-input-error" id="value-input-error" role="alert">{valueError}</p>}

            {parsedQuery.numericValue !== undefined && (
              <div className="value-details">
                {!evaluation.grade && evaluation.message && <p className="value-message">{evaluation.message}</p>}
                {evaluation.unitChoices && (
                  <div className="unit-choice" aria-label="Choose unit">
                    <span>Choose unit</span>
                    {evaluation.unitChoices.map((unit) => (
                      <button type="button" key={unit} onClick={() => changeUnit(unit)}>{unit}</button>
                    ))}
                  </div>
                )}
                {!evaluation.grade && referencesToShow.length > 0 && (
                  <div className="reference-fields">
                    {referencesToShow.map((reference) => (
                      <label key={reference.key}>
                        <span>{reference.label}{reference.optional ? " (optional)" : ""}</span>
                        <input
                          inputMode="decimal"
                          type="number"
                          step="any"
                          value={references[reference.key] ?? ""}
                          onChange={(event) => onReferenceValue(reference.key, event.target.value === "" ? undefined : Number(event.target.value))}
                          aria-label={reference.label}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {parsedQuery.numericValue !== undefined && !record.numericGrading && (
        <p className="clinical-only">This term uses clinical grading criteria. The entered value is not used to assign a grade.</p>
      )}

      <section className="criteria-section" aria-labelledby="criteria-heading">
        <div className="section-title-row">
          <div>
            <span className="section-kicker">Source criteria</span>
            <h2 id="criteria-heading">{gradeCriteriaLabel(record.grades)}</h2>
          </div>
        </div>
        <GradeCards
          record={record}
          automaticGrade={evaluation.grade}
          possibleGrades={evaluation.possibleGrades}
        />
      </section>

      {record.navigationalNote && (
        <details className="navigation-note">
          <summary>
            <span>
              <span className="section-kicker">Coding guidance</span>
              <strong>Navigational note</strong>
            </span>
          </summary>
          <p><SourceText text={record.navigationalNote} crossReferences={record.crossReferences} onOpenReference={onOpenReference} /></p>
        </details>
      )}

      <button className="source-link" type="button" onClick={onOpenPdf}>
        <span><small>Source</small>CTCAE v{record.version} · p. {record.source.page}</span>
        <strong>View PDF</strong>
      </button>
    </article>
  );
}
